import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ApiTestForm from './ApiTestForm';
import ApiLogPanel from './ApiLogPanel';
import DebugPanel from './DebugPanel';
import { logger } from '../utils/logger';

const { FiMonitor, FiDatabase, FiCode, FiSettings, FiActivity, FiLogOut } = FiIcons;

function ApiMonitorDashboard({ onLogout }) {
  const [activeTab, setActiveTab] = useState('ncb');
  const [logs, setLogs] = useState([]);
  const [isLogPanelOpen, setIsLogPanelOpen] = useState(true);

  useEffect(() => {
    const unsubscribe = logger.subscribe((updatedLogs) => {
      setLogs(updatedLogs);
    });

    return unsubscribe;
  }, []);

  const tabs = [
    { id: 'ncb', label: 'NoCodeBackend', icon: FiDatabase, color: 'text-blue-600' },
    { id: 'dropbox', label: 'Dropbox API', icon: FiCode, color: 'text-green-600' },
    { id: 'debug', label: 'Debug Panel', icon: FiSettings, color: 'text-purple-600' }
  ];

  const handleLogout = () => {
    logger.info('User initiated logout');
    onLogout();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Left side */}
            <div className="flex items-center space-x-4">
              <SafeIcon icon={FiMonitor} className="h-8 w-8 text-primary-600" />
              <div>
                <h1 className="text-xl font-semibold text-gray-900">API Monitor Platform</h1>
                <p className="text-sm text-gray-500">Real-time API testing & logging</p>
              </div>
            </div>

            {/* Right side */}
            <div className="flex items-center space-x-4">
              {/* Log Panel Toggle */}
              <button
                onClick={() => setIsLogPanelOpen(!isLogPanelOpen)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isLogPanelOpen
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <SafeIcon icon={FiActivity} className="h-4 w-4" />
                <span>Logs ({logs.length})</span>
              </button>

              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
              >
                <SafeIcon icon={FiLogOut} className="h-4 w-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className={`grid gap-6 ${isLogPanelOpen ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
          {/* Left Column - Main Panel */}
          <div className="space-y-6">
            {/* Tab Navigation */}
            <div className="bg-white rounded-xl shadow-sm border p-1">
              <nav className="flex space-x-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      activeTab === tab.id
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <SafeIcon icon={tab.icon} className={`h-4 w-4 ${activeTab === tab.id ? 'text-white' : tab.color}`} />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div>
              {activeTab === 'debug' ? (
                <DebugPanel />
              ) : (
                <ApiTestForm apiType={activeTab} />
              )}
            </div>
          </div>

          {/* Right Column - Log Panel */}
          <AnimatePresence>
            {isLogPanelOpen && (
              <motion.div
                initial={{ opacity: 0, x: 300 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 300 }}
                transition={{ duration: 0.3 }}
                className="lg:block"
              >
                <ApiLogPanel logs={logs} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}

export default ApiMonitorDashboard;